import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Briefcase, 
  Award, 
  Star, 
  Mail, 
  Phone,
  ExternalLink,
  Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

const UserProfileDetailPage = () => {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [user_id]);

  const fetchProfile = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/users/${user_id}`);
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        toast.error('Profile not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-accent border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-muted-foreground">Profile not found</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Navigation showBackButton={true} backTo="/" />

      {/* Profile Content */}
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Profile Header */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center text-5xl font-bold text-white">
                    {profile.name.charAt(0)}
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-3xl font-bold mb-2">{profile.name}</h1>
                      <p className="text-xl text-accent font-medium">{profile.designation}</p>
                    </div>
                    {profile.verified && (
                      <Badge className="bg-accent/10 text-accent border-accent/20">
                        <Award className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      <span>{profile.experience} years experience</span>
                    </div>
                    {profile.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{profile.location.city}, {profile.location.state}</span>
                      </div>
                    )}
                    {profile.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span>{profile.rating.toFixed(1)} ({profile.reviews} reviews)</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Departments */}
                  {profile.departments && profile.departments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profile.departments.map(dept => (
                        <Badge key={dept} variant="secondary">
                          {dept.replace(/-/g, ' ').toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-3 mt-4">
                    {profile.phone && (
                      <Button variant="outline" size="sm">
                        <Phone className="w-4 h-4 mr-2" />
                        {profile.phone}
                      </Button>
                    )}
                    {profile.email && (
                      <Button variant="outline" size="sm">
                        <Mail className="w-4 h-4 mr-2" />
                        {profile.email}
                      </Button>
                    )}
                    {profile.imdb_link && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(profile.imdb_link, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        IMDb Profile
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bio */}
          {profile.bio && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3">About</h2>
                <p className="text-muted-foreground">{profile.bio}</p>
              </CardContent>
            </Card>
          )}

          {/* Skills */}
          {profile.skills && profile.skills.length > 0 && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-accent" />
                  Skills & Expertise
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map(skill => (
                    <Badge key={skill} className="bg-accent/10 text-accent border-accent/20">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Previous Projects */}
          {profile.previous_projects && profile.previous_projects.length > 0 && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3">Previous Projects</h2>
                <ul className="space-y-2">
                  {profile.previous_projects.map((project, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-accent mt-1">•</span>
                      <span className="text-muted-foreground">{project}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Portfolio Gallery */}
          {profile.portfolio && profile.portfolio.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-accent" />
                  Portfolio Gallery
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {profile.portfolio.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                      className="relative group cursor-pointer"
                      onClick={() => setSelectedImage(item)}
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                        alt={item.title || `Portfolio ${idx + 1}`}
                        className="w-full h-48 object-cover rounded-lg border border-border group-hover:border-accent transition-all"
                      />
                      {item.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 rounded-b-lg">
                          <p className="text-white text-sm font-medium truncate">
                            {item.title}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl w-full">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20"
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </Button>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${selectedImage.image_url}`}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />
            {selectedImage.title && (
              <p className="text-white text-center mt-4 text-lg">
                {selectedImage.title}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDetailPage;
